// @flow

import React, {useEffect, useState} from 'react';
import CustomModal from '../../../../../elements/CustomModal/CustomModal';
import {
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Typography,
  makeStyles,
} from '@material-ui/core';

import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import {colors} from '../../../../../assets/styles/colors';

import CustomTextField from '../../../../../elements/CustomTextField/CustomTextField';
import {
  AddPhotoAlternateOutlined,
  Close,
  ColorizeOutlined,
} from '@material-ui/icons';

import {getImageUrl} from '../../../../../utils/image_provider';
import CropModal from '../../../../../elements/CropModal';
import ColorOptionModal from '../ColorOptionModal/ColorOptionModal';

import {uploadImageService} from '../../../../file-storage/promises';
import {updateProductVariantOptionService} from '../../../promises';
import {createFile} from '../../../../../utils/image';

import type {Theme} from '@material-ui/core';
import type {
  ProductVariantOption,
  ProductVariantOptionDisplayType,
  ProductVariantOptionImageShape,
} from '../../../../../entities';

import type {OptionValue} from '../EditVariantModal/EditVariantModal';
import SnackBar from '../../../../../elements/SnackBar/SnackBar';
import {classNames} from '../../../../../utils/react';
import {useAlert} from '../../../../../elements/Alert/AlertContext';
type Props = {
  variantOptionId: string,
  imageShape: ProductVariantOptionImageShape,
  displayType: ProductVariantOptionDisplayType,
  optionValues: OptionValue[],
  handleClose: () => void,
  onSaveSuccess: (v: ProductVariantOption) => void,
};

const useStyles = makeStyles((theme: Theme) => ({
  modal: {
    '& .MuiPaper-root': {
      padding: theme.spacing(2),
    },
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: 500,
    padding: theme.spacing(2),
  },
  title: {
    textAlign: 'center',
    fontWeight: 600,
    fontSize: '24px',
    marginTop: theme.spacing(2),
  },
  buttonWrap: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  text: {
    color: colors.grey900,
    fontSize: 14,
    fontWeight: 600,
  },
  divider: {
    width: '100%',
    margin: theme.spacing(2, 0),
    background: colors.grey300,
  },
  wrapVariants: {
    width: '100%',
    padding: theme.spacing(2),
    borderRadius: '8px',
    border: `1px solid ${colors.grey300}`,
    marginBottom: theme.spacing(1),
  },
  deleteIcon: {
    color: '#E54D7B',
  },
  editVariantRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  buttonPreview: {
    padding: 0,
    width: 48,
    height: 48,
    position: 'relative',
  },
  colorWrap: {
    width: 48,
    height: 48,
  },
  addImage: {
    cursor: 'pointer',
    border: `2px dashed ${colors.grey500}`,
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    opacity: 0.8,
    width: 48,
    height: 48,
    minWidth: 48,

    padding: 0,
  },
  removeImage: {
    top: -8,
    right: -8,
    position: 'absolute',
    background: '#FFFFFF',
    border: '1px solid #E8E8ED',
  },
  imgPreview: {
    maxWidth: '48px !important',
    maxHeight: '48px !important',
    width: '48px !important',
    height: '48px !important',
  },
  noImage: {
    height: 48,
    width: 48,
  },
  loadingWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  imageBox: {
    display: 'flex',
    height: 48,
    width: 48,
    minWidth: 48,
    justifyContent: 'center',
    alignSelf: 'center',
    alignItems: 'center',
    textAlign: 'center',
    position: 'relative',
    border: `2px dashed ${colors.grey500}`,
    borderRadius: '50%',
    overflow: 'hidden',
  },
  addImageBox: {
    opacity: 0.8,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    // marginLeft: theme.spacing(1),
    // marginRight: theme.spacing(1),
    gap: '.25rem',

    '&:hover': {
      opacity: 1,
      backgroundColor: colors.grey200,
    },
  },
  existImageBox: {
    border: 'none',
    '& img': {
      width: '100%',
      height: '100%',
    },
  },
}));

export type CropImage = {
  file: File,
  preview: string,
  aspect?: number,
};

type EditOptionImageProps = {
  imageShape: ProductVariantOptionImageShape,
  displayType: ProductVariantOptionDisplayType,
  optionValue: OptionValue,
  setNewValue: (v: OptionValue) => void,
};

export const EditOptionImage = ({
  displayType,
  optionValue,
  imageShape,
  setNewValue,
}: EditOptionImageProps) => {
  const [openColorModal, setOpenColorModal] = useState<boolean>(false);
  const [imageToCrop, setImageToCrop] = useState<CropImage | null>(null);

  const isColor = displayType === 'COLOR';
  const classes = useStyles();
  const {showAlert} = useAlert();

  const shouldSaveImage = !isColor;

  const buildPreview = () => {
    if (isColor)
      return (
        <div
          className={classes.colorWrap}
          style={{backgroundColor: optionValue.color}}
        ></div>
      );

    if (optionValue.imageId)
      return (
        <img
          className={classes.imgPreview}
          src={getImageUrl(optionValue.imageId)}
          alt=""
        />
      );

    if (optionValue.file)
      return (
        <img
          className={classes.imgPreview}
          src={URL.createObjectURL(optionValue.file)}
          alt=""
        />
      );
    else return null;
  };

  const handleNewImage = () => {
    setOpenColorModal(true);
  };

  const handleUploadImage = async (
    e: SyntheticInputEvent<HTMLInputElement>
  ) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tamaño de archivo (1MB = 1048576 bytes)
    const MAX_FILE_SIZE = 1048576; // 1MB
    if (file.size > MAX_FILE_SIZE) {
      const fileSizeInMB = (file.size / 1048576).toFixed(2);
      showAlert(
        `La imagen es demasiado grande (${fileSizeInMB}MB). El tamaño máximo permitido es 1MB. Por favor, comprime la imagen antes de subirla.`,
        {type: 'warning', clearPreviousAlerts: true}
      );
      e.target.value = ''; // Limpiar el input
      return;
    }

    setImageToCrop({file: file, preview: URL.createObjectURL(file)});
  };

  const handleSelectColor = (color: string) => {
    setNewValue({...optionValue, color});
  };

  const handleRemoveImage = () => {
    const nValue = {...optionValue};
    const key = isColor ? 'color' : 'file';
    nValue[key] = undefined;
    nValue.editFile = false;
    nValue.imageId = undefined;
    setNewValue(nValue);
  };

  const shouldAddImage = isColor ? !optionValue.color : !optionValue.file;
  return (
    <>
      <input
        hidden
        accept="image/png, image/jpeg"
        type="file"
        onChange={handleUploadImage}
      />
      <ColorOptionModal
        open={openColorModal}
        handleClose={() => setOpenColorModal(false)}
        onSelectColor={handleSelectColor}
      />
      {imageToCrop && (
        <CropModal
          open
          aspect={1}
          onClose={() => {
            setImageToCrop(null);
          }}
          images={[imageToCrop]}
          compressOptions={{
            quality: 0.8,
            maxWidth: 3840,
            maxHeight: 2160,
          }}
          keepFormat
          onSubmit={results => {
            if (results?.[0])
              setNewValue({
                ...optionValue,
                file: results[0].file,
                editFile: true,
              });
            setImageToCrop(null);
          }}
        />
      )}

      {shouldAddImage &&
        (shouldSaveImage ? (
          <label
            className={classNames(classes.imageBox, classes.addImageBox, {
              [classes.existImageBox]: !!imageToCrop?.preview,
            })}
            aria-label="subir imagen"
          >
            <input
              hidden
              accept="image/png, image/jpeg"
              type="file"
              onChange={handleUploadImage}
            />

            {imageToCrop?.preview ? (
              <img src={imageToCrop?.preview} style={{width: '48px'}} alt="" />
            ) : (
              <AddPhotoAlternateOutlined color="disabled" />
            )}
          </label>
        ) : (
          <Button
            className={classes.addImage}
            onClick={handleNewImage}
            aria-label="seleccionar color"
          >
            <span className={classNames(classes.imageBox, classes.addImageBox)}>
              <ColorizeOutlined color="disabled" />
            </span>
          </Button>
        ))}

      {!shouldAddImage && (
        <div
          className={classes.buttonPreview}
          style={{borderRadius: imageShape === 'CIRCULAR' ? '50%' : undefined}}
        >
          <IconButton
            size="small"
            className={classes.removeImage}
            onClick={handleRemoveImage}
          >
            <Close fontSize="small" />
          </IconButton>
          {buildPreview()}
        </div>
      )}
    </>
  );
};

type Status = 'ERROR' | 'LOADING' | 'SAVING';
export default function ConfirmVariantEditModal({
  variantOptionId,
  optionValues,
  handleClose,
  displayType,
  imageShape,
  onSaveSuccess,
}: Props) {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [status, setStatus] = useState<Status | null>(null);
  const [edittingValues, setEdittingValues] = useState<OptionValue[]>([]);

  const fillInitialValues = async (optionValues: OptionValue[]) => {
    const length = optionValues.length;
    const draft = [];
    setIsLoading(true);
    for (let i = 0; i < length; i++) {
      let item = optionValues[i];
      if (!item.file) {
        if (item.imageId) {
          try {
            const {file} = await createFile(getImageUrl(item.imageId));
            item = {...item, file};
          } catch (err) {
            item = {...item, file: new File([], 'test.jpg')};
          }
        }
      }
      draft[i] = item;
    }

    setIsLoading(false);
    setEdittingValues(draft);
  };

  useEffect(() => {
    // For avoiding references issues
    fillInitialValues(optionValues);
  }, [optionValues]);

  const handleOptionValueName = (index: number, name: string) => {
    setEdittingValues(ps => {
      const aux = [...ps];
      const item = aux[index];
      aux[index][item.id ? 'label' : 'name'] = name;
      return aux;
    });
  };

  // const handleRemoveOption = (index: number) => {
  //   setEdittingValues(ps => {
  //     const aux = [...ps];
  //     aux.splice(index, 1);
  //     return aux;
  //   });
  // };

  const getUpdatedValues = () => {
    const length = optionValues.length;

    const valuesToEdit = [];
    for (let i = 0; i < length; i++) {
      const currValue = optionValues[i];
      const newValue = edittingValues[i];
      // file handler
      let fileBody: {[key: string]: any} = {
        id: currValue.id,
        name: currValue.id ? newValue.label : newValue.name,
        color: newValue.color,
      };
      if (newValue.imageId) {
        if (newValue.editFile) {
          fileBody.file = newValue.file;
        } else if (newValue.imageId && newValue.file) {
          fileBody.imageId = newValue.imageId;
        }
      } else {
        if (newValue.file) fileBody.file = newValue.file;
      }

      valuesToEdit.push(fileBody);
    }

    return valuesToEdit;
  };

  const handleEditOptionValues = async () => {
    const newValues = getUpdatedValues();

    const filesMap = {};

    const filteredNewValues = newValues.filter(
      el => Object.keys(el).length > 0
    );

    filteredNewValues.forEach((o, idx) => {
      if (o.file) filesMap[idx] = o.file;
    });

    const values = filteredNewValues.map(op => {
      delete op.preview;
      delete op.file;
      delete op.enabled;
      const {imageId, name, id, color} = op;

      const newObj: {[key: string]: any} = {
        color: color || undefined,
        name,
        imageId: imageId !== undefined ? imageId : undefined,
      };
      if (id) {
        newObj.productVariantOptionValueId = id;
      }
      return newObj;
    });

    const keysFilesMap = Object.keys(filesMap);
    if (keysFilesMap.length > 0) {
      setStatus('SAVING');
      const filesToUpload = Object.values(filesMap);
      try {
        const imagesArr = await uploadImageService(
          // $FlowFixMe
          filesToUpload,
          'im/product-variant-value'
        );

        keysFilesMap.forEach((k, idx) => {
          values[Number(k)].imageId = imagesArr[idx].id;
        });
      } catch (err) {
        console.log(err);
      }
    }

    const request = {
      // imageShape: imageShape,
      // displayType: displayType,
      values,
    };
    setStatus('SAVING');
    updateProductVariantOptionService(request, variantOptionId)
      .then(r => {
        if (r) {
          onSaveSuccess(r);
          handleClose();
        }
      })
      .catch(err => {
        setStatus('ERROR');
        console.log(err);
      });
  };

  return (
    <CustomModal open className={classes.modal} handleCloseModal={handleClose}>
      <SnackBar
        mode="error"
        message={status === 'ERROR' ? 'Error al editar la variante.' : ''}
      />

      <div className={classes.root}>
        <Typography className={classes.title}>Confirmar edición</Typography>
        <Divider className={classes.divider} />
        <Typography className={classes.text} style={{marginBottom: '1rem'}}>
          Recuerda que al editar la variante se modificará para todos los
          productos que la usen.
        </Typography>
        <div className={classes.wrapVariants}>
          <Grid container spacing={2}>
            {isLoading && (
              <div className={classes.loadingWrapper}>
                <CircularProgress />
              </div>
            )}
            {edittingValues.map((v, idx) => (
              <Grid
                key={'variant-to-edit-' + idx}
                item
                xs={12}
                className={classes.editVariantRow}
              >
                <EditOptionImage
                  imageShape={imageShape}
                  displayType={displayType}
                  setNewValue={newValue => {
                    let aux = [...edittingValues];
                    aux[idx] = newValue;
                    setEdittingValues(aux);
                  }}
                  optionValue={v}
                />
                <div>
                  <CustomTextField
                    value={v.id ? v.label : v.name}
                    onChange={e => handleOptionValueName(idx, e.target.value)}
                  />
                </div>
              </Grid>
            ))}
          </Grid>
        </div>
        <div className={classes.buttonWrap}>
          <CustomButton
            disabled={['SAVING'].includes(status)}
            variant="contained"
            onClick={handleEditOptionValues || isLoading}
          >
            Editar
          </CustomButton>
        </div>
      </div>
    </CustomModal>
  );
}
